import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "../../api/axios";
import { useTranslation } from "react-i18next";
import useAuth from "../../hooks/useAuth";
import { useNavigate } from "react-router-dom";
import { getThemeColor } from "../../utils/applyTheme";

const CustomGoogleSignIn = () => {
  const { setAuth } = useAuth();
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const [errorMsg, setErrorMsg] = useState("");
  const [isInCooldown, setIsInCooldown] = useState(false);
  const [cooldownEndTime, setCooldownEndTime] = useState(null);
  const [remainingTime, setRemainingTime] = useState(0);
  const googleButtonRef = useRef(null);
  const cooldownTimerRef = useRef(null);
  const countdownIntervalRef = useRef(null);
  const isInitializedRef = useRef(false);

  const handleCredentialResponse = useCallback(async (response) => {
    const idToken = response.credential;

    // Clear any cooldown state on successful response
    setIsInCooldown(false);
    setCooldownEndTime(null);
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    try {
      const res = await axios.post(
        "/Login/StaffGoogleLogin",
        JSON.stringify({ token: idToken }),
        { headers: { "Content-Type": "application/json" }, withCredentials: true }
      );
      setErrorMsg("");
      // Handle both AccessToken (capital A) and accessToken (lowercase) from backend
      const accessToken = res.data.AccessToken || res.data.accessToken;
      setAuth({
        userId: res.data.userId,
        user: res.data.username,
        fullName: res.data.fullName,
        email: res.data.email,
        role: res.data.role,
        universityId: res.data.universityId,
        universityName: res.data.universityName,
        accessToken: accessToken,
        lastSuccessfulLoginDate: res.data.lastSuccessfulLoginDate,
      });
      localStorage.setItem("persist", "true");
      const loginLangOverride = sessionStorage.getItem("umsLoginLangOverride");
      if (loginLangOverride) {
        i18n.changeLanguage(loginLangOverride);
        sessionStorage.removeItem("umsLoginLangOverride");
        axios.get(`/Home/SetLanguage/${loginLangOverride}`, { withCredentials: true });
      } else {
        i18n.changeLanguage(res.data.lang);
      }
      navigate("/Home", { replace: true });
    } catch (err) {
      let backendMsg = "Login failed. Please try again.";
      let shouldStartCooldown = false;

      if (!err.response) {
        backendMsg = "No server response. Please check your internet connection and try again.";
      } else if (err.response.status === 429) {
        // Rate limit exceeded
        const retryAfter = err.response.data?.retryAfter ||
                          parseInt(err.response.headers['retry-after']) ||
                          60;
        backendMsg = err.response.data?.message ||
                     `Too many login attempts. Please wait ${retryAfter} seconds before trying again.`;
        shouldStartCooldown = true;
        startCooldown(retryAfter);
      } else if (err.response.data) {
        if (typeof err.response.data === 'string') {
          backendMsg = err.response.data;
        } else if (err.response.data.message) {
          backendMsg = err.response.data.message;
        } else if (err.response.data.error) {
          backendMsg = err.response.data.error;
        }
      } else {
        backendMsg = err.message || "An unexpected error occurred. Please try again.";
      }

      setErrorMsg(backendMsg);

      if (!shouldStartCooldown) {
        // Only clear cooldown if we didn't just start a new one
        setIsInCooldown(false);
        setCooldownEndTime(null);
      }
    }
  }, [setAuth, i18n, navigate]);

  const startCooldown = useCallback((seconds = 30) => {
    const cooldownDuration = seconds * 1000;
    const endTime = Date.now() + cooldownDuration;

    setIsInCooldown(true);
    setCooldownEndTime(endTime);
    setRemainingTime(seconds);

    // Clear any existing timers
    if (cooldownTimerRef.current) {
      clearTimeout(cooldownTimerRef.current);
    }
    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current);
    }

    // Update countdown every second
    countdownIntervalRef.current = setInterval(() => {
      const remaining = Math.ceil((endTime - Date.now()) / 1000);
      if (remaining <= 0) {
        clearInterval(countdownIntervalRef.current);
        setRemainingTime(0);
      } else {
        setRemainingTime(remaining);
      }
    }, 1000);

    // Set timer to clear cooldown
    cooldownTimerRef.current = setTimeout(() => {
      setIsInCooldown(false);
      setCooldownEndTime(null);
      setRemainingTime(0);
      setErrorMsg("");
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    }, cooldownDuration);
  }, []);

  const initializeGoogle = useCallback(() => {
    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      return;
    }

    if (isInitializedRef.current) {
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id:
          window.__UMS_THEME__?.googleClientId || process.env.REACT_APP_GOOGLE_CLIENT_ID || "768848829644-b0nnkjedupd0mk2t0uocn3gotuod25u5.apps.googleusercontent.com",
        callback: handleCredentialResponse,
        auto_select: false,
        cancel_on_tap_outside: true,
      });

      // Render the Google button with custom styling
      if (googleButtonRef.current) {
        window.google.accounts.id.renderButton(
          googleButtonRef.current,
          {
            theme: "outline",
            size: "large",
            width: googleButtonRef.current.offsetWidth,
            text: "continue_with",
            shape: "rectangular",
            logo_alignment: "left"
          }
        );
      }

      isInitializedRef.current = true;
    } catch (error) {
      console.error("Error initializing Google Sign-In:", error);
      setErrorMsg("Failed to initialize Google Sign-In. Please refresh the page.");
    }
  }, [handleCredentialResponse]);

  useEffect(() => {
    const loadGoogleScript = () => {
      if (!window.google) {
        const script = document.createElement("script");
        script.src = "https://accounts.google.com/gsi/client";
        script.async = true;
        script.defer = true;
        script.onload = () => {
          initializeGoogle();
        };
        script.onerror = () => {
          setErrorMsg("Failed to load Google Sign-In. Please check your internet connection.");
        };
        document.body.appendChild(script);
        
        return () => {
          if (document.body.contains(script)) {
            document.body.removeChild(script);
          }
        };
      } else {
        initializeGoogle();
      }
    };

    const cleanup = loadGoogleScript();
    
    return () => {
      if (cleanup) cleanup();
      if (cooldownTimerRef.current) {
        clearTimeout(cooldownTimerRef.current);
      }
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current);
      }
    };
  }, [initializeGoogle]);

  // Fallback click handler for custom button
  const onSignInClick = useCallback(() => {
    if (isInCooldown) {
      const remainingTime = Math.ceil((cooldownEndTime - Date.now()) / 1000);
      setErrorMsg(`Please wait ${remainingTime} seconds before trying again.`);
      return;
    }

    setErrorMsg("");

    if (!window.google || !window.google.accounts || !window.google.accounts.id) {
      setErrorMsg("Google Sign-In is not loaded. Please refresh the page.");
      return;
    }

    try {
      // Prompt the Google Sign-In dialog
      window.google.accounts.id.prompt((notification) => {
        if (notification.isNotDisplayed() || notification.isSkippedMoment()) {
          // Only show a message if the popup was blocked or skipped
          // Don't start cooldown for user-initiated cancellations
          console.log("Google Sign-In notification:", notification.getNotDisplayedReason() || notification.getSkippedReason());
        }
      });
    } catch (error) {
      console.error("Error prompting Google Sign-In:", error);
      setErrorMsg("Unable to open Google Sign-In. Please try again or refresh the page.");
    }
  }, [isInCooldown, cooldownEndTime]);

  return (
    <div style={{ width: '100%' }}>
      {/* Google Sign-In Button Container */}
      <div style={{ position: 'relative', marginTop: '10px' }}>
        {/* Google's native button - styled with wrapper */}
        <div 
          style={{ 
            display: isInCooldown ? 'none' : 'block',
            width: '100%',
            position: 'relative',
            borderRadius: '8px',
            overflow: 'hidden',
            transition: 'all 0.3s ease',
            boxShadow: '0 1px 3px rgba(0,0,0,0.12)',
          }}
          onMouseEnter={(e) => {
            if (!isInCooldown) {
              e.currentTarget.style.boxShadow = `0 4px 12px ${getThemeColor('primary', '#dc3545')}26`;
              e.currentTarget.style.transform = 'translateY(-1px)';
            }
          }}
          onMouseLeave={(e) => {
            if (!isInCooldown) {
              e.currentTarget.style.boxShadow = '0 1px 3px rgba(0,0,0,0.12)';
              e.currentTarget.style.transform = 'translateY(0)';
            }
          }}
        >
          <div 
            ref={googleButtonRef}
            style={{ 
              width: '100%',
              minHeight: '48px',
            }}
          />
        </div>
        
        {/* Cooldown button with modern styling */}
        {isInCooldown && (
          <button
            onClick={onSignInClick}
            disabled={isInCooldown}
            style={{
              backgroundColor: '#fff5f5',
              color: getThemeColor('primary', '#dc3545'),
              border: '2px solid #ffebee',
              width: '100%',
              padding: '14px 20px',
              fontSize: '15px',
              fontWeight: '500',
              cursor: 'not-allowed',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '8px',
              transition: 'all 0.3s ease',
              minHeight: '48px',
            }}
          >
            <svg
              style={{
                width: '20px',
                height: '20px',
                marginRight: '10px',
                animation: 'pulse 1.5s ease-in-out infinite'
              }}
              viewBox="0 0 24 24"
            >
              <style>
                {`@keyframes pulse {
                  0%, 100% { opacity: 1; }
                  50% { opacity: 0.4; }
                }`}
              </style>
              <path
                fill={getThemeColor('primary', '#dc3545')}
                d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z"
                opacity="0.3"
              />
              <path
                fill={getThemeColor('primary', '#dc3545')}
                d="M11.99 2C6.47 2 2 6.48 2 12s4.47 10 9.99 10C17.52 22 22 17.52 22 12S17.52 2 11.99 2zM12 20c-4.42 0-8-3.58-8-8s3.58-8 8-8 8 3.58 8 8-3.58 8-8 8zm.5-13H11v6l5.25 3.15.75-1.23-4.5-2.67z"
              />
            </svg>
            Please wait {remainingTime} second{remainingTime !== 1 ? 's' : ''}...
          </button>
        )}
      </div>
      
      {/* Error message with modern styling */}
      {errorMsg && (
        <div style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#fff5f5',
          border: '1px solid #ffebee',
          borderRadius: '6px',
          color: getThemeColor('primary', '#dc3545'),
          fontSize: '14px',
          lineHeight: '1.5',
          display: 'flex',
          alignItems: 'flex-start',
          animation: 'slideIn 0.3s ease'
        }}>
          <style>
            {`@keyframes slideIn { from { opacity: 0; transform: translateY(-10px); } to { opacity: 1; transform: translateY(0); } }`}
          </style>
          <svg 
            style={{ 
              width: '18px', 
              height: '18px', 
              marginRight: '10px',
              flexShrink: 0,
              marginTop: '1px'
            }}
            viewBox="0 0 24 24"
          >
            <path
              fill={getThemeColor('primary', '#dc3545')}
              d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"
            />
          </svg>
          <span>{errorMsg}</span>
        </div>
      )}

      {/* Security notice */}
      <p style={{
        marginTop: '20px',
        marginBottom: '0',
        fontSize: '12px',
        color: '#999',
        textAlign: 'center',
        lineHeight: '1.5'
      }}>
        <svg 
          style={{ 
            width: '14px', 
            height: '14px', 
            marginRight: '5px',
            verticalAlign: 'middle',
            marginTop: '-2px'
          }}
          viewBox="0 0 24 24"
        >
          <path 
            fill="#999" 
            d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
          />
        </svg>
        Secure login powered by Google
      </p>
    </div>
  );
};

export default CustomGoogleSignIn;
